<script setup lang="ts">
import OperationOfficeAutoComplete from '@/components/AutoCompletes/SingleOperationOfficeAutoComplete.vue'
import AccountAutoComplete from '@/components/AutoCompletes/AccountAutoComplete.vue';
import ValidationErrors from '@/components/Common/ValidationErrors.vue';
import RangeDatePicker from '@/components/Common/RangeDatePicker.vue';
import getOperationOffice from '@/types/responses/getOperationOffice';
import getAccountResponse from '@/types/responses/getAccount';
import { Ref, reactive, ref, computed, watch } from "vue";
import {
  SELECTABLE_AUDITABLE_TYPE_ITEMS,
  SELECTABLE_EVENT_ITEMS
} from '@/services/consts';
import i18n from "@/lang/i18n"

const emits = defineEmits(['search', 'formchange'])

const props = defineProps<{
  isLoading: boolean
  errors: {
    operation_office_id: string[],
    daterange: string[]
  }
}>()

type formType = {
  page: number,
  auditable_type: string | null,
  auditable_id: number | null,
  event: string | null,
  operationOffice: getOperationOffice | undefined
  account: getAccountResponse | undefined,
  daterange: Date[]
}

const DEFAULT_BACK_DAYS = 6
const TODAY = new Date(new Date().getFullYear(), new Date().getMonth(), new Date().getDate())
const BEFORE_DAY = new Date(TODAY.getFullYear(), TODAY.getMonth(), TODAY.getDate() - DEFAULT_BACK_DAYS)
const hide: Ref<boolean> = ref(false)
const form: formType = reactive({
  page: 1,
  auditable_type: null,
  auditable_id: null,
  operationOffice: undefined,
  event: null,
  account: undefined,
  daterange: [
    BEFORE_DAY,
    TODAY
  ]
})

const title = computed(() => {
  return hide.value ? "" : i18n.global.t('pages.common.filter.title')
})

const searchAreaClass = computed(() => {
  return hide.value ? "search-area-hide p-4" : "search-area p-4"
})

const changeSearchButton = computed(() => {
  return props.isLoading ? i18n.global.t('pages.common.button.loading') : i18n.global.t('pages.common.button.exec')
})

function clearForm () {
  form.auditable_type = null
  form.auditable_id = null
  form.event = ''
  form.operationOffice = undefined
  form.account = undefined
  form.daterange = [
    BEFORE_DAY,
    TODAY
  ]
}

function toggleSidebar () {
  hide.value = !hide.value
}

function handleClickSearch() {
  emits('search', form)
}

watch(form, () => {
  emits('formchange', form)
})
</script>
<template>
  <div :class="searchAreaClass">
    <h4 class="d-flex justify-content-between mt-0">
      {{ title }}

      <svg
        width="24px"
        height="24px"
        viewBox="0 0 24 24"
        fit=""
        preserveAspectRatio="xMidYMid meet"
        focusable="false"
        sandboxuid="0"
        @click="toggleSidebar"
      >
        <template v-if="hide">
          <path d="M7.615 11.795l4.59-4.59-1.41-1.41-6 6 6 6 1.41-1.41-4.59-4.59zm9.59 6v-12h-2v12h2z" fill-rule="evenodd" sandboxuid="0"></path>
        </template>
        <template v-if="!hide">
          <path d="M6 7.41L10.438 12 6 16.59 7.363 18l5.802-6-5.802-6L6 7.41zM16.066 6H18v12h-1.934V6z" sandboxuid="0" />
        </template>
      </svg>
    </h4>
    <div :class="`strpied-tabled-with-hover ${hide ? 'd-none' : '' }`">
      <div class="d-grid gap-2">
        <OperationOfficeAutoComplete
          v-model="form.operationOffice"
          :error-messages="props.errors.operation_office_id"
          selectFirstIfOnlyOne
          :clearable="false"
        />
        <AccountAutoComplete
          v-model="form.account"
          :operationOfficeId="form.operationOffice?.id"
          :operationOfficeOnly="true"
        />
        <v-select
          v-model="form.auditable_type"
          :items="SELECTABLE_AUDITABLE_TYPE_ITEMS"
          item-title="name"
          item-value="value"
          :label="i18n.global.t('pages.auditLog.list.auditableType')"
        />
        <v-select
          v-model="form.event"
          :items="SELECTABLE_EVENT_ITEMS"
          class="mb-2"
          item-title="name"
          item-value="value"
          :label="i18n.global.t('pages.auditLog.list.event')"
        />
      </div>
      <div class="d-flex flex-column">
        <validation-errors :messages="props.errors.daterange">
          <div style="font-size: 12px" class="d-flex justify-space-between">
            <RangeDatePicker
              v-model="form.daterange"
              :maxRange="DEFAULT_BACK_DAYS"
              :format="'yyyy/MM/dd'"
              :isError="props.errors.daterange?.length > 0"
            />
          </div>
        </validation-errors>
        <v-divider
          :thickness="3"
          class="my-2"
        />
        <div class="d-flex search-buttons" >
          <v-btn
            class="ma-2 exec-btn"
            @click="handleClickSearch"
            :disabled="props.isLoading"
            :style="{
              backgroundColor : props.isLoading ? '#B3B8BB' : ''
            }"
          >
            {{ changeSearchButton }}
          </v-btn>
          <v-btn
            class="ma-2 reset-btn"
            :disabled="props.isLoading"
            @click="clearForm"
          >
            {{ i18n.global.t('pages.common.button.reset') }}
          </v-btn>
        </div>
      </div>
    </div>
  </div>
</template>
